import { BroadcastChannel, createLeaderElection, LeaderElector } from 'broadcast-channel';
import { EventEmitter, type UnsubscribeFn } from './eventEmitter';
import type {
  BaseBroadcastMessage,
  ITabSyncManager,
  LeadershipChangeCallback,
  VisibilityChangeCallback,
  TabSyncConfig,
} from './types';

const DEFAULT_CONFIG: Required<TabSyncConfig> = {
  channelName: 'afflo-tab-sync',
  leaderElectionConfig: {
    // How long to wait for response when checking for existing leader
    responseTime: 1000,
    // How often to check if the leader is still alive
    fallbackInterval: 2000,
  },
};

type TabSyncEvents = {
  leadershipChange: [{ isLeader: boolean }];
  visibilityChange: [{ isVisible: boolean }];
};

export class TabSyncManager implements ITabSyncManager {
  private static instance: TabSyncManager | null = null;

  private broadcastChannel: BroadcastChannel<BaseBroadcastMessage> | null = null;
  private leaderElector: LeaderElector | null = null;
  private _isLeader = false;
  private _isInitialized = false;
  private config: Required<TabSyncConfig>;

  private eventEmitter = new EventEmitter<TabSyncEvents>();
  private channelSubscribers: Map<string, Set<(msg: BaseBroadcastMessage) => void>> = new Map();

  private boundBeforeUnload: (() => void) | null = null;
  private boundVisibilityChange: (() => void) | null = null;

  private constructor(config?: TabSyncConfig) {
    this.config = {
      channelName: config?.channelName ?? DEFAULT_CONFIG.channelName,
      leaderElectionConfig: {
        ...DEFAULT_CONFIG.leaderElectionConfig,
        ...config?.leaderElectionConfig,
      },
    };
  }

  public static getInstance(config?: TabSyncConfig): TabSyncManager {
    if (!TabSyncManager.instance) {
      TabSyncManager.instance = new TabSyncManager(config);
    }
    return TabSyncManager.instance;
  }

  public get isLeader(): boolean {
    return this._isLeader;
  }

  public get isInitialized(): boolean {
    return this._isInitialized;
  }

  public async initialize(): Promise<void> {
    if (this._isInitialized) {
      return;
    }

    this._isLeader = false;

    this.broadcastChannel = new BroadcastChannel(this.config.channelName);

    this.broadcastChannel.onmessage = (msg) => {
      this.handleBroadcastMessage(msg);
    };

    this.leaderElector = createLeaderElection(
      this.broadcastChannel,
      this.config.leaderElectionConfig
    );

    this.leaderElector.onduplicate = () => {
      this._isLeader = false;
      this.eventEmitter.emit('leadershipChange', { isLeader: false });
    };

    this.boundBeforeUnload = () => {
      this.handleBeforeUnload();
    };

    this.boundVisibilityChange = () => {
      this.handleVisibilityChange();
    };

    window.addEventListener('beforeunload', this.boundBeforeUnload);
    document.addEventListener('visibilitychange', this.boundVisibilityChange);

    this.leaderElector.awaitLeadership().then(() => {
      this._isLeader = true;
      this.eventEmitter.emit('leadershipChange', { isLeader: true });
    });

    this._isInitialized = true;
  }

  private handleBeforeUnload(): void {
    if (this.broadcastChannel) {
      this.leaderElector?.die().catch(() => {
        // Channel already closed
      });
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
  }

  private handleVisibilityChange(): void {
    const isVisible = !document.hidden;
    this.eventEmitter.emit('visibilityChange', { isVisible });
  }

  private handleBroadcastMessage(message: BaseBroadcastMessage): void {
    const { channel } = message;

    if (!channel) {
      return;
    }

    const subscribers = this.channelSubscribers.get(channel);
    if (!subscribers || subscribers.size === 0) {
      return;
    }

    for (const callback of subscribers) {
      try {
        callback(message);
      } catch (error) {
        console.error(`[TabSyncManager] Error in subscriber for channel "${channel}":`, error);
      }
    }
  }

  // Resolves once the message is sent, wait for it before leaving the page so the message is not lost
  public broadcast<T extends BaseBroadcastMessage>(message: T): Promise<void> {
    if (!this.broadcastChannel) {
      return Promise.resolve();
    }

    try {
      return this.broadcastChannel.postMessage(message).catch(() => {
        // Silently ignore broadcast errors
      });
    } catch {
      // Silently ignore broadcast errors
      return Promise.resolve();
    }
  }

  public subscribe<T extends BaseBroadcastMessage>(
    channel: string,
    callback: (msg: T) => void
  ): UnsubscribeFn {
    if (!this.channelSubscribers.has(channel)) {
      this.channelSubscribers.set(channel, new Set());
    }

    const subscribers = this.channelSubscribers.get(channel)!;
    const wrappedCallback = callback as (msg: BaseBroadcastMessage) => void;
    subscribers.add(wrappedCallback);

    return () => {
      subscribers.delete(wrappedCallback);
    };
  }

  public onLeadershipChange(callback: LeadershipChangeCallback): UnsubscribeFn {
    return this.eventEmitter.on('leadershipChange', callback);
  }

  public onVisibilityChange(callback: VisibilityChangeCallback): UnsubscribeFn {
    return this.eventEmitter.on('visibilityChange', callback);
  }

  public destroy(): void {

    if (this.boundBeforeUnload) {
      window.removeEventListener('beforeunload', this.boundBeforeUnload);
      this.boundBeforeUnload = null;
    }

    if (this.boundVisibilityChange) {
      document.removeEventListener('visibilitychange', this.boundVisibilityChange);
      this.boundVisibilityChange = null;
    }

    this.leaderElector?.die().catch(() => {
      // Channel may be closed
    });

    this.broadcastChannel?.close();
    this.broadcastChannel = null;
    this.leaderElector = null;

    this.eventEmitter.removeAllListeners();
    this.channelSubscribers.clear();

    this._isLeader = false;
    this._isInitialized = false;

    TabSyncManager.instance = null;
  }
}

export function useTabSyncManager(config?: TabSyncConfig): TabSyncManager {
  return TabSyncManager.getInstance(config);
}
